import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Modal,
  Stack,
  Table,
  Text,
  NumberInput,
  Textarea,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconTargetArrow,
  IconPencil,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { useEffect, useState, type FormEvent } from 'react';
import { PageHeader } from '@/components/page-header';
import { ApiError, ApiErrorCode, NetworkError } from '@/lib/api-error';
import {
  createMotivation,
  deleteMotivation,
  listMotivations,
  updateMotivation,
  type Motivation,
  type MotivationDraft,
} from '@/lib/motivations';
import classes from './settings-page.module.css';

/** What the form is for: adding a motivation, or editing the one it holds. */
type Subject = { mode: 'create' } | { mode: 'edit'; motivation: Motivation };

function describeError(error: unknown): string {
  if (error instanceof NetworkError) {
    return 'The API did not answer. It may not be running.';
  }
  if (error instanceof ApiError) return error.message;
  return 'Something went wrong.';
}

/**
 * Client-side checks, mirroring what the server enforces.
 *
 * A convenience so a typo does not cost a round trip — not a control. This
 * bundle is public and modifiable, so the API validates the same fields again
 * and is the only thing that decides what gets stored.
 */
function validate(
  draft: MotivationDraft,
): Partial<Record<keyof MotivationDraft, string>> {
  const errors: Partial<Record<keyof MotivationDraft, string>> = {};

  if (!draft.label.trim()) errors.label = 'Required.';
  if (!draft.promise.trim()) errors.promise = 'Required.';

  const code = draft.code.trim();
  if (!code) {
    errors.code = 'Required.';
  } else if (!/^[a-zA-Z]{2,3}(-[a-zA-Z]{2,4})?$/.test(code)) {
    errors.code = 'A slug, such as moving or trip.';
  }

  return errors;
}

interface MotivationFormProps {
  subject: Subject;
  onClose: () => void;
  onSaved: (motivation: Motivation, mode: Subject['mode']) => void;
}

/**
 * One form for both adding and editing.
 *
 * The two differ by their title, their submit label and whether an id is being
 * patched. Splitting them in two would duplicate three inputs and their
 * validation to express that.
 *
 * Mounted per subject with a `key`, so the fields start from the right values
 * and an abandoned edit cannot bleed into the next one — the alternative is an
 * effect that resets four pieces of state on every open.
 */
function MotivationForm({ subject, onClose, onSaved }: MotivationFormProps) {
  const [draft, setDraft] = useState<MotivationDraft>(() =>
    subject.mode === 'edit'
      ? {
          label: subject.motivation.label,
          code: subject.motivation.code,
          promise: subject.motivation.promise,
          displayOrder: subject.motivation.displayOrder,
        }
      : { label: '', code: '', promise: '', displayOrder: null },
  );
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof MotivationDraft, string>>
  >({});
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [isSaving, setIsSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();

    const errors = validate(draft);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const trimmed: MotivationDraft = {
      label: draft.label.trim(),
      code: draft.code.trim(),
      promise: draft.promise.trim(),
      displayOrder: draft.displayOrder,
    };

    setIsSaving(true);
    setSubmitError(null);
    try {
      const saved =
        subject.mode === 'edit'
          ? await updateMotivation(subject.motivation.id, trimmed)
          : await createMotivation(trimmed);
      onSaved(saved, subject.mode);
      onClose();
    } catch (caught) {
      setSubmitError(caught);
    } finally {
      setIsSaving(false);
    }
  }

  // A conflict is about one field and belongs beside it, not in a banner at
  // the top of a three-field form.
  const conflict =
    submitError instanceof ApiError && submitError.is(ApiErrorCode.CONFLICT)
      ? submitError.message
      : null;

  return (
    <form onSubmit={(event) => void submit(event)} noValidate>
      <Stack gap="md">
        {submitError !== null && conflict === null && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            <Text size="sm">{describeError(submitError)}</Text>
          </Alert>
        )}

        <TextInput
          label="Reason"
          description="What the chip says, in the learner's own voice."
          placeholder="I'm moving there"
          required
          data-autofocus
          value={draft.label}
          error={fieldErrors.label}
          onChange={(event) =>
            setDraft({ ...draft, label: event.currentTarget.value })
          }
        />

        <TextInput
          label="Code"
          description="A stable slug. Answers are recorded against it, so changing one orphans every answer already given."
          placeholder="moving"
          required
          value={draft.code}
          error={fieldErrors.code ?? conflict ?? undefined}
          onChange={(event) =>
            setDraft({ ...draft, code: event.currentTarget.value })
          }
        />

        <Textarea
          autosize
          minRows={2}
          label="Promise"
          description="What the first run closes on for somebody who picks this. A sentence, not a phrase — it is the last thing the app says."
          placeholder="Two weeks from now you'll be able to order, ask for directions, and complain about the weather."
          required
          dir="auto"
          value={draft.promise}
          error={fieldErrors.promise}
          onChange={(event) =>
            setDraft({ ...draft, promise: event.currentTarget.value })
          }
        />

        <NumberInput
          label="Order"
          description="Lower comes first. Leave it blank and the reason sits at the end."
          placeholder="Unranked"
          min={0}
          max={9999}
          allowDecimal={false}
          value={draft.displayOrder ?? ''}
          error={fieldErrors.displayOrder}
          onChange={(value) =>
            setDraft({
              ...draft,
              displayOrder: value === '' ? null : Number(value),
            })
          }
        />

        <Group justify="flex-end" gap="sm">
          <Button
            variant="subtle"
            color="gray"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button type="submit" loading={isSaving}>
            {subject.mode === 'edit' ? 'Save changes' : 'Add motivation'}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

interface RemoveFormProps {
  motivation: Motivation;
  onClose: () => void;
  onRemoved: (motivation: Motivation) => void;
}

/**
 * Confirmation before a delete.
 *
 * The delete is a hard one — nothing references a motivation yet, so there is no
 * soft-delete column to set — which makes a mis-click unrecoverable except by
 * typing the row back in.
 */
function RemoveForm({ motivation, onClose, onRemoved }: RemoveFormProps) {
  const [error, setError] = useState<unknown>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  async function confirm() {
    setIsRemoving(true);
    try {
      await deleteMotivation(motivation.id);
      onRemoved(motivation);
      onClose();
    } catch (caught) {
      setError(caught);
    } finally {
      setIsRemoving(false);
    }
  }

  return (
    <Stack gap="md">
      {error !== null && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          <Text size="sm">{describeError(error)}</Text>
        </Alert>
      )}

      <Text size="sm">
        {motivation.label} leaves the catalogue. Nothing else on the platform
        points at a motivation yet, so this deletes the row outright — getting it
        back means adding it again.
      </Text>

      <Group justify="flex-end" gap="sm">
        <Button
          variant="subtle"
          color="gray"
          onClick={onClose}
          disabled={isRemoving}
        >
          Cancel
        </Button>
        <Button color="red" loading={isRemoving} onClick={() => void confirm()}>
          Remove
        </Button>
      </Group>
    </Stack>
  );
}

export function MotivationsPage() {
  const [motivations, setMotivations] = useState<Motivation[]>([]);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(true);
  // What each dialog is about, and whether it is open, held separately.
  // Clearing the subject on close would empty the dialog while it is still
  // fading out; leaving it set until the next open keeps a close looking like
  // a close.
  const [subject, setSubject] = useState<Subject | null>(null);
  const [isFormOpen, setFormOpen] = useState(false);
  const [removing, setRemoving] = useState<Motivation | null>(null);
  const [isRemoveOpen, setRemoveOpen] = useState(false);

  function openForm(next: Subject) {
    setSubject(next);
    setFormOpen(true);
  }

  function openRemove(motivation: Motivation) {
    setRemoving(motivation);
    setRemoveOpen(true);
  }

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const list = await listMotivations();
        if (cancelled) return;
        setMotivations(list);
        setLoadError(null);
      } catch (caught) {
        if (!cancelled) setLoadError(caught);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * The order the API sorts by, held locally so an edit lands in place.
   *
   * It has to match `CATALOGUE_ORDER` in the API exactly: display order first
   * with unranked at the back, then label. Get this wrong and a saved motivation
   * appears in one place until the page is reloaded and it moves.
   */
  function sorted(list: Motivation[]): Motivation[] {
    return [...list].sort((a, b) => {
      if (a.displayOrder !== b.displayOrder) {
        if (a.displayOrder === null) return 1;
        if (b.displayOrder === null) return -1;
        return a.displayOrder - b.displayOrder;
      }
      return a.label.localeCompare(b.label);
    });
  }

  function onSaved(saved: Motivation, mode: Subject['mode']) {
    setMotivations((current) =>
      sorted(
        mode === 'edit'
          ? current.map((item) => (item.id === saved.id ? saved : item))
          : [...current, saved],
      ),
    );
    notifications.show({
      color: 'brand',
      message:
        mode === 'edit' ? `${saved.label} updated.` : `${saved.label} added.`,
    });
  }

  function onRemoved(removed: Motivation) {
    setMotivations((current) => current.filter((item) => item.id !== removed.id));
    notifications.show({ color: 'gray', message: `${removed.label} removed.` });
  }

  const header = (
    <PageHeader
      title="Reasons"
      description="What Fluently can teach. Learners choose from this list."
      actions={
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => openForm({ mode: 'create' })}
          disabled={isLoading || loadError !== null}
        >
          Add motivation
        </Button>
      }
    />
  );

  if (isLoading) {
    return (
      <Stack gap="lg" className={classes.page}>
        {header}
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      </Stack>
    );
  }

  if (loadError !== null) {
    return (
      <Stack gap="lg" className={classes.page}>
        {header}
        <Alert
          color="red"
          title="Cannot load the reasons"
          icon={<IconAlertTriangle size={18} />}
        >
          <Text size="sm">{describeError(loadError)}</Text>
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="lg" className={classes.page}>
      {header}

      {motivations.length === 0 ? (
        <Card>
          <Stack gap="xs" align="center" py="xl">
            <IconTargetArrow size={28} stroke={1.4} />
            <Text fw={600}>No motivations yet</Text>
            <Text size="sm" c="dimmed" ta="center" maw={420}>
              The catalogue is empty. Add the first motivation and it becomes
              available to everything that reads this list.
            </Text>
          </Stack>
        </Card>
      ) : (
        <Card p={0}>
          <Table.ScrollContainer minWidth={520}>
            <Table verticalSpacing="sm" horizontalSpacing="lg">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={80}>Order</Table.Th>
                  <Table.Th>Reason</Table.Th>
                  <Table.Th w={110}>Code</Table.Th>
                  <Table.Th>Promise</Table.Th>
                  <Table.Th w={90} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {motivations.map((motivation) => (
                  <Table.Tr key={motivation.id}>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {motivation.displayOrder ?? '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={600}>
                        {motivation.label}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        variant="light"
                        color="gray"
                        className={classes.code}
                      >
                        {motivation.code.toUpperCase()}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {/* The last thing the first run says to somebody who
                          picks this reason, so it is shown in full rather than
                          truncated — it is the field most worth reading. */}
                      <Text size="sm" c="dimmed">
                        {motivation.promise}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} justify="flex-end" wrap="nowrap">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          aria-label={`Edit ${motivation.label}`}
                          onClick={() => openForm({ mode: 'edit', motivation })}
                        >
                          <IconPencil size={16} stroke={1.7} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          aria-label={`Remove ${motivation.label}`}
                          onClick={() => openRemove(motivation)}
                        >
                          <IconTrash size={16} stroke={1.7} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}

      <Text size="xs" c="dimmed">
        These are offered when the first run asks what somebody is learning for,
        in this order. The promise is the last thing the app says to them, so it
        is worth reading aloud. Anyone who types their own reason instead is
        recorded too &mdash; those are the ones worth adding here.
      </Text>

      <Modal
        opened={isFormOpen}
        onClose={() => setFormOpen(false)}
        centered
        title={
          subject?.mode === 'edit'
            ? `Edit ${subject.motivation.label}`
            : 'Add a motivation'
        }
      >
        {subject && (
          <MotivationForm
            key={subject.mode === 'edit' ? subject.motivation.id : 'create'}
            subject={subject}
            onClose={() => setFormOpen(false)}
            onSaved={onSaved}
          />
        )}
      </Modal>

      <Modal
        opened={isRemoveOpen}
        onClose={() => setRemoveOpen(false)}
        centered
        title={`Remove ${removing?.label ?? 'motivation'}?`}
      >
        {removing && (
          <RemoveForm
            key={removing.id}
            motivation={removing}
            onClose={() => setRemoveOpen(false)}
            onRemoved={onRemoved}
          />
        )}
      </Modal>
    </Stack>
  );
}
